import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import httpClient, { extractApiError } from "../api/httpClient";
import { dataGridSx, PageHeader, PagePanel, PageShell, pageTypography } from "../components/PageLayout";
import { useUI } from "../contexts/UIContext";

const defaultLocale = {
  id: null,
  nome: "",
  codice: "",
  indirizzo: "",
  citta: "",
  cap: "",
  telefono: "",
  email: "",
  attivo: true,
};

const dayOptions = [
  { value: "MONDAY", label: "Lunedì" },
  { value: "TUESDAY", label: "Martedì" },
  { value: "WEDNESDAY", label: "Mercoledì" },
  { value: "THURSDAY", label: "Giovedì" },
  { value: "FRIDAY", label: "Venerdì" },
  { value: "SATURDAY", label: "Sabato" },
  { value: "SUNDAY", label: "Domenica" },
];

const normalizeDayOfWeek = (value) => {
  if (!value) {
    return "";
  }

  const normalized = String(value).trim().toUpperCase();
  const option = dayOptions.find((day) => day.value === normalized || day.label.toUpperCase() === normalized);
  return option?.value || normalized;
};

const defaultOrario = {
  id: null,
  dayOfWeek: "MONDAY",
  oraApertura: "09:00",
  oraChiusura: "18:00",
  aperto: true,
};

const mansioneOptions = ["CUCINA", "PIZZERIA", "CAMERIERE", "CASSA", "RIDER"];
const fasciaOptions = ["PRANZO", "CENA"];

const defaultStaffConfig = {
  id: null,
  giornoSettimana: "MONDAY",
  mansione: "CAMERIERE",
  fascia: "PRANZO",
  minPersone: 0,
  maxPersone: 999,
};

const LOCALI_EXPORT_SCHEMA_VERSION = 1;
const LOCALE_FIELDS = ["nome", "codice", "indirizzo", "citta", "cap", "telefono", "email", "attivo"];

const sanitizeFileToken = (value) =>
  String(value || "locale")
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase() || "locale";

const downloadJsonFile = (filename, payload) => {
  const blob = new Blob([`${JSON.stringify(payload, null, 2)}\n`], {
    type: "application/json;charset=utf-8",
  });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
};

function LocaleModal({ open, value, onClose, onSubmit }) {
  const [form, setForm] = useState(defaultLocale);

  useEffect(() => {
    setForm(value || defaultLocale);
  }, [value]);

  const updateField = (field) => (event) => {
    const fieldValue = field === "attivo" ? event.target.value === "true" : event.target.value;
    setForm((prev) => ({ ...prev, [field]: fieldValue }));
  };

  const handleSubmit = () => {
    if (!form.nome || !form.codice) {
      return;
    }

    onSubmit({
      ...form,
      nome: form.nome.trim(),
      codice: form.codice.trim(),
      indirizzo: form.indirizzo?.trim() || "",
      citta: form.citta?.trim() || "",
      cap: form.cap?.trim() || "",
      telefono: form.telefono?.trim() || "",
      email: form.email?.trim() || "",
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{form.id ? "Modifica locale" : "Nuovo locale"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Nome" value={form.nome} onChange={updateField("nome")} required />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Codice" value={form.codice} onChange={updateField("codice")} required />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField fullWidth label="Indirizzo" value={form.indirizzo} onChange={updateField("indirizzo")} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Citta" value={form.citta} onChange={updateField("citta")} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="CAP" value={form.cap} onChange={updateField("cap")} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Telefono" value={form.telefono} onChange={updateField("telefono")} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Email" value={form.email} onChange={updateField("email")} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField select fullWidth label="Stato" value={String(form.attivo)} onChange={updateField("attivo")}>
              <MenuItem value="true">Attivo</MenuItem>
              <MenuItem value="false">Disattivo</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="contained" onClick={handleSubmit}>Salva</Button>
      </DialogActions>
    </Dialog>
  );
}

function OrarioModal({ open, value, onClose, onSubmit }) {
  const [form, setForm] = useState(defaultOrario);

  useEffect(() => {
    if (!value) {
      setForm(defaultOrario);
      return;
    }

    setForm({
      ...defaultOrario,
      ...value,
      dayOfWeek: normalizeDayOfWeek(value.dayOfWeek),
    });
  }, [value]);

  const updateField = (field) => (event) => {
    const fieldValue = field === "aperto" ? event.target.value === "true" : event.target.value;
    setForm((prev) => ({ ...prev, [field]: fieldValue }));
  };

  const handleSubmit = () => {
    if (!form.dayOfWeek) {
      return;
    }

    onSubmit({
      ...form,
      dayOfWeek: normalizeDayOfWeek(form.dayOfWeek),
      oraApertura: form.oraApertura || null,
      oraChiusura: form.oraChiusura || null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{form.id ? "Modifica orario" : "Nuovo orario"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField select fullWidth label="Giorno" value={form.dayOfWeek} onChange={updateField("dayOfWeek")}>
              {dayOptions.map((day) => (
                <MenuItem key={day.value} value={day.value}>
                  {day.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="time"
              label="Apertura"
              value={form.oraApertura || ""}
              onChange={updateField("oraApertura")}
              InputLabelProps={{ shrink: true }}
              disabled={!form.aperto}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="time"
              label="Chiusura"
              value={form.oraChiusura || ""}
              onChange={updateField("oraChiusura")}
              InputLabelProps={{ shrink: true }}
              disabled={!form.aperto}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField select fullWidth label="Aperto" value={String(form.aperto)} onChange={updateField("aperto")}>
              <MenuItem value="true">Si</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="contained" onClick={handleSubmit}>Salva</Button>
      </DialogActions>
    </Dialog>
  );
}

function StaffConfigModal({ open, value, onClose, onSubmit }) {
  const [form, setForm] = useState(defaultStaffConfig);

  useEffect(() => {
    setForm(value || defaultStaffConfig);
  }, [value]);

  const updateField = (field) => (event) => {
    const v = ["minPersone", "maxPersone"].includes(field) ? Number(event.target.value) : event.target.value;
    setForm((prev) => ({ ...prev, [field]: v }));
  };

  const handleSubmit = () => {
    if (!form.giornoSettimana || !form.mansione || !form.fascia) return;
    onSubmit({ ...form, minPersone: Number(form.minPersone), maxPersone: Number(form.maxPersone) });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{form.id ? "Modifica configurazione staff" : "Nuova configurazione staff"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Giorno" value={form.giornoSettimana} onChange={updateField("giornoSettimana")}>
              {dayOptions.map((day) => (
                <MenuItem key={day.value} value={day.value}>{day.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Fascia" value={form.fascia} onChange={updateField("fascia")}>
              {fasciaOptions.map((f) => (
                <MenuItem key={f} value={f}>{f}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField select fullWidth label="Mansione" value={form.mansione} onChange={updateField("mansione")}>
              {mansioneOptions.map((m) => (
                <MenuItem key={m} value={m}>{m}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Min dipendenti"
              value={form.minPersone}
              onChange={updateField("minPersone")}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Max dipendenti"
              value={form.maxPersone}
              onChange={updateField("maxPersone")}
              inputProps={{ min: 0 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="contained" onClick={handleSubmit}>Salva</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Locali() {
  const [loading, setLoading] = useState(true);
  const [locali, setLocali] = useState([]);
  const [selectedLocaleId, setSelectedLocaleId] = useState(null);
  const [orari, setOrari] = useState([]);
  const [localeModal, setLocaleModal] = useState({ open: false, value: defaultLocale });
  const [orarioModal, setOrarioModal] = useState({ open: false, value: defaultOrario });
  const [staffConfigs, setStaffConfigs] = useState([]);
  const [staffConfigModal, setStaffConfigModal] = useState({ open: false, value: defaultStaffConfig });
  const [orariDuplicaSourceLocaleId, setOrariDuplicaSourceLocaleId] = useState("");
  const [orariDuplicaOpen, setOrariDuplicaOpen] = useState(false);
  const [staffDuplicaSourceLocaleId, setStaffDuplicaSourceLocaleId] = useState("");
  const [staffDuplicaOpen, setStaffDuplicaOpen] = useState(false);
  const [deleteHourModal, setDeleteHourModal] = useState({ open: false, value: null });
  const localiImportInputRef = useRef(null);
  const orariImportInputRef = useRef(null);
  const staffImportInputRef = useRef(null);

  const { showNotification } = useUI();

  const selectedLocale = useMemo(
    () => locali.find((locale) => Number(locale.id) === Number(selectedLocaleId)) || null,
    [locali, selectedLocaleId]
  );

  const loadLocali = useCallback(async () => {
    setLoading(true);
    try {
      const response = await httpClient.get("/locali");
      const data = response?.data || [];
      setLocali(data);
      if (data.length > 0) {
        setSelectedLocaleId((prev) => prev || data[0].id);
      } else {
        setSelectedLocaleId(null);
      }
    } catch (error) {
      showNotification(extractApiError(error), "error");
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  const loadOrari = useCallback(
    async (localeId) => {
      if (!localeId) {
        setOrari([]);
        return;
      }

      try {
        const response = await httpClient.get(`/locali/${localeId}/orari`);
        setOrari(response?.data || []);
      } catch (error) {
        showNotification(extractApiError(error), "error");
      }
    },
    [showNotification]
  );

  const loadStaffConfig = useCallback(
    async (localeId) => {
      if (!localeId) {
        setStaffConfigs([]);
        return;
      }

      try {
        const response = await httpClient.get(`/locali/${localeId}/staff-config`);
        setStaffConfigs(response?.data || []);
      } catch (error) {
        showNotification(extractApiError(error), "error");
      }
    },
    [showNotification]
  );

  useEffect(() => {
    loadLocali();
  }, [loadLocali]);

  useEffect(() => {
    loadOrari(selectedLocaleId);
  }, [selectedLocaleId, loadOrari]);

  useEffect(() => {
    loadStaffConfig(selectedLocaleId);
  }, [selectedLocaleId, loadStaffConfig]);

  const saveLocale = async (payload) => {
    try {
      if (payload.id) {
        await httpClient.put(`/locali/${payload.id}`, payload);
        showNotification("Locale aggiornato", "success");
      } else {
        await httpClient.post("/locali", payload);
        showNotification("Locale creato", "success");
      }

      setLocaleModal({ open: false, value: defaultLocale });
      await loadLocali();
    } catch (error) {
      showNotification(extractApiError(error), "error");
    }
  };

  const deleteLocale = async (id) => {
    try {
      await httpClient.delete(`/locali/${id}`);
      showNotification("Locale disattivato", "success");
      await loadLocali();
    } catch (error) {
      showNotification(extractApiError(error), "error");
    }
  };

  const saveOrario = async (payload) => {
    if (!selectedLocaleId) {
      return;
    }

    try {
      const dayOfWeek = normalizeDayOfWeek(payload.dayOfWeek);
      const body = {
        dayOfWeek,
        oraApertura: payload.aperto ? payload.oraApertura : null,
        oraChiusura: payload.aperto ? payload.oraChiusura : null,
        aperto: payload.aperto,
      };

      if (payload.id) {
        await httpClient.put(`/locali/${selectedLocaleId}/orari/${dayOfWeek}`, body);
        showNotification("Orario aggiornato", "success");
      } else {
        await httpClient.post(`/locali/${selectedLocaleId}/orari`, body);
        showNotification("Orario creato", "success");
      }

      setOrarioModal({ open: false, value: defaultOrario });
      await loadOrari(selectedLocaleId);
    } catch (error) {
      showNotification(extractApiError(error), "error");
    }
  };

  const deleteOrario = async (dayOfWeek) => {
    if (!selectedLocaleId || !dayOfWeek) {
      return;
    }

    try {
      const normalizedDay = normalizeDayOfWeek(dayOfWeek);
      await httpClient.delete(`/locali/${selectedLocaleId}/orari/${normalizedDay}`);
      showNotification("Orario eliminato", "success");
      await loadOrari(selectedLocaleId);
    } catch (error) {
      showNotification(extractApiError(error), "error");
    }
  };

  const saveStaffConfig = async (payload) => {
    if (!selectedLocaleId) return;

    try {
      const body = {
        giornoSettimana: payload.giornoSettimana,
        mansione: payload.mansione,
        fascia: payload.fascia,
        minPersone: Number(payload.minPersone),
        maxPersone: Number(payload.maxPersone),
      };

      if (payload.id) {
        await httpClient.put(`/locali/${selectedLocaleId}/staff-config/${payload.id}`, body);
        showNotification("Configurazione aggiornata", "success");
      } else {
        await httpClient.post(`/locali/${selectedLocaleId}/staff-config`, body);
        showNotification("Configurazione creata", "success");
      }

      setStaffConfigModal({ open: false, value: defaultStaffConfig });
      await loadStaffConfig(selectedLocaleId);
    } catch (error) {
      showNotification(extractApiError(error), "error");
    }
  };

  const deleteStaffConfig = async (id) => {
    try {
      await httpClient.delete(`/locali/${selectedLocaleId}/staff-config/${id}`);
      showNotification("Configurazione eliminata", "success");
      await loadStaffConfig(selectedLocaleId);
    } catch (error) {
      showNotification(extractApiError(error), "error");
    }
  };

  const duplicateLocaleFromRow = (localeRow) => {
    if (!localeRow) {
      return;
    }

    setLocaleModal({
      open: true,
      value: {
        ...defaultLocale,
        nome: localeRow.nome ? `${localeRow.nome} (Copia)` : "",
        codice: localeRow.codice ? `${localeRow.codice}-COPY` : "",
        indirizzo: localeRow.indirizzo || "",
        citta: localeRow.citta || "",
        cap: localeRow.cap || "",
        telefono: localeRow.telefono || "",
        email: localeRow.email || "",
        attivo: true,
      },
    });
  };

  const duplicateOrariFromLocale = async () => {
    if (!selectedLocaleId || !orariDuplicaSourceLocaleId) {
      return;
    }

    const sourceId = Number(orariDuplicaSourceLocaleId);
    const targetId = Number(selectedLocaleId);
    if (!sourceId || sourceId === targetId) {
      showNotification("Seleziona un locale sorgente diverso", "warning");
      return;
    }

    try {
      const sourceResponse = await httpClient.get(`/locali/${sourceId}/orari`);
      const sourceOrari = sourceResponse?.data || [];
      if (!sourceOrari.length) {
        showNotification("Il locale sorgente non ha orari da duplicare", "warning");
        return;
      }

      const currentByDay = new Map((orari || []).map((item) => [item.dayOfWeek, item]));

      for (const item of sourceOrari) {
        const dayOfWeek = normalizeDayOfWeek(item.dayOfWeek);
        const body = {
          dayOfWeek,
          oraApertura: item.aperto ? item.oraApertura : null,
          oraChiusura: item.aperto ? item.oraChiusura : null,
          aperto: Boolean(item.aperto),
        };

        if (currentByDay.has(dayOfWeek)) {
          await httpClient.put(`/locali/${targetId}/orari/${dayOfWeek}`, body);
        } else {
          await httpClient.post(`/locali/${targetId}/orari`, body);
        }
      }

      setOrariDuplicaOpen(false);
      setOrariDuplicaSourceLocaleId("");
      await loadOrari(targetId);
      showNotification("Orari duplicati con successo", "success");
    } catch (error) {
      showNotification(extractApiError(error), "error");
    }
  };

  const duplicateStaffConfigFromLocale = async () => {
    if (!selectedLocaleId || !staffDuplicaSourceLocaleId) {
      return;
    }

    const sourceId = Number(staffDuplicaSourceLocaleId);
    const targetId = Number(selectedLocaleId);
    if (!sourceId || sourceId === targetId) {
      showNotification("Seleziona un locale sorgente diverso", "warning");
      return;
    }

    try {
      const sourceResponse = await httpClient.get(`/locali/${sourceId}/staff-config`);
      const sourceConfigs = sourceResponse?.data || [];
      if (!sourceConfigs.length) {
        showNotification("Il locale sorgente non ha configurazioni da duplicare", "warning");
        return;
      }

      const keyOf = (row) => `${row.giornoSettimana}|${row.fascia}|${row.mansione}`;
      const targetByKey = new Map((staffConfigs || []).map((row) => [keyOf(row), row]));

      for (const config of sourceConfigs) {
        const body = {
          giornoSettimana: config.giornoSettimana,
          mansione: config.mansione,
          fascia: config.fascia,
          minPersone: Number(config.minPersone),
          maxPersone: Number(config.maxPersone),
        };

        const existing = targetByKey.get(keyOf(config));
        if (existing?.id) {
          await httpClient.put(`/locali/${targetId}/staff-config/${existing.id}`, body);
        } else {
          await httpClient.post(`/locali/${targetId}/staff-config`, body);
        }
      }

      setStaffDuplicaOpen(false);
      setStaffDuplicaSourceLocaleId("");
      await loadStaffConfig(targetId);
      showNotification("Configurazione staff duplicata con successo", "success");
    } catch (error) {
      showNotification(extractApiError(error), "error");
    }
  };

  const readJsonFromFile = async (file) => {
    if (!file) {
      throw new Error("Nessun file selezionato.");
    }

    const content = await file.text();
    if (!content.trim()) {
      throw new Error("Il file JSON è vuoto.");
    }

    return JSON.parse(content);
  };

  const openImportDialog = (inputRef) => {
    if (!inputRef?.current) {
      return;
    }
    inputRef.current.value = "";
    inputRef.current.click();
  };

  const exportLocaliJson = () => {
    const localesPayload = (locali || []).map((locale) => {
      const next = {};
      LOCALE_FIELDS.forEach((field) => {
        next[field] = field === "attivo" ? Boolean(locale?.[field]) : locale?.[field] || "";
      });
      return next;
    });

    downloadJsonFile("locali-export.json", {
      schemaVersion: LOCALI_EXPORT_SCHEMA_VERSION,
      section: "locali",
      exportedAt: new Date().toISOString(),
      locali: localesPayload,
    });
    showNotification("Export locali completato", "success");
  };

  const importLocaliJson = async (event) => {
    const selectedFile = event?.target?.files?.[0];
    if (!selectedFile) {
      return;
    }

    try {
      const parsed = await readJsonFromFile(selectedFile);
      const entries = Array.isArray(parsed)
        ? parsed
        : Array.isArray(parsed?.locali)
          ? parsed.locali
          : [];

      if (!entries.length) {
        throw new Error("Il file non contiene locali da importare.");
      }

      const byCode = new Map(
        (locali || [])
          .filter((locale) => locale?.codice)
          .map((locale) => [String(locale.codice).trim().toUpperCase(), locale])
      );

      let created = 0;
      let updated = 0;
      for (const rawLocale of entries) {
        const nome = String(rawLocale?.nome || "").trim();
        const codice = String(rawLocale?.codice || "").trim();
        if (!nome || !codice) {
          throw new Error("Ogni locale deve contenere almeno nome e codice.");
        }

        const payload = {
          nome,
          codice,
          indirizzo: String(rawLocale?.indirizzo || "").trim(),
          citta: String(rawLocale?.citta || "").trim(),
          cap: String(rawLocale?.cap || "").trim(),
          telefono: String(rawLocale?.telefono || "").trim(),
          email: String(rawLocale?.email || "").trim(),
          attivo: typeof rawLocale?.attivo === "boolean" ? rawLocale.attivo : true,
        };

        const existing = byCode.get(codice.toUpperCase());
        if (existing?.id) {
          await httpClient.put(`/locali/${existing.id}`, payload);
          updated += 1;
        } else {
          await httpClient.post("/locali", payload);
          created += 1;
        }
      }

      await loadLocali();
      showNotification(`Import locali completato (${created} creati, ${updated} aggiornati)`, "success");
    } catch (error) {
      showNotification(extractApiError(error), "error");
    }
  };

  const exportOrariJson = () => {
    if (!selectedLocaleId || !selectedLocale) {
      showNotification("Seleziona un locale prima di esportare gli orari", "warning");
      return;
    }

    const payload = (orari || []).map((item) => ({
      dayOfWeek: normalizeDayOfWeek(item?.dayOfWeek),
      oraApertura: item?.oraApertura || null,
      oraChiusura: item?.oraChiusura || null,
      aperto: Boolean(item?.aperto),
    }));

    downloadJsonFile(`orari-${sanitizeFileToken(selectedLocale.nome || selectedLocale.codice)}.json`, {
      schemaVersion: LOCALI_EXPORT_SCHEMA_VERSION,
      section: "orari",
      localeId: selectedLocaleId,
      localeCodice: selectedLocale.codice || "",
      exportedAt: new Date().toISOString(),
      orari: payload,
    });
    showNotification("Export orari completato", "success");
  };

  const importOrariJson = async (event) => {
    const selectedFile = event?.target?.files?.[0];
    if (!selectedFile) {
      return;
    }
    if (!selectedLocaleId) {
      showNotification("Seleziona un locale prima di importare gli orari", "warning");
      return;
    }

    try {
      const parsed = await readJsonFromFile(selectedFile);
      const entries = Array.isArray(parsed)
        ? parsed
        : Array.isArray(parsed?.orari)
          ? parsed.orari
          : [];

      if (!entries.length) {
        throw new Error("Il file non contiene orari da importare.");
      }

      const currentByDay = new Map((orari || []).map((item) => [normalizeDayOfWeek(item.dayOfWeek), item]));
      let created = 0;
      let updated = 0;

      for (const rawItem of entries) {
        const dayOfWeek = normalizeDayOfWeek(rawItem?.dayOfWeek);
        if (!dayOfWeek) {
          throw new Error("Ogni orario deve avere dayOfWeek valorizzato.");
        }

        const body = {
          dayOfWeek,
          oraApertura: rawItem?.aperto === false ? null : rawItem?.oraApertura || null,
          oraChiusura: rawItem?.aperto === false ? null : rawItem?.oraChiusura || null,
          aperto: rawItem?.aperto !== false,
        };

        if (currentByDay.has(dayOfWeek)) {
          await httpClient.put(`/locali/${selectedLocaleId}/orari/${dayOfWeek}`, body);
          updated += 1;
        } else {
          await httpClient.post(`/locali/${selectedLocaleId}/orari`, body);
          created += 1;
        }
      }

      await loadOrari(selectedLocaleId);
      showNotification(`Import orari completato (${created} creati, ${updated} aggiornati)`, "success");
    } catch (error) {
      showNotification(extractApiError(error), "error");
    }
  };

  const exportStaffJson = () => {
    if (!selectedLocaleId || !selectedLocale) {
      showNotification("Seleziona un locale prima di esportare lo staff", "warning");
      return;
    }

    const payload = (staffConfigs || []).map((item) => ({
      giornoSettimana: normalizeDayOfWeek(item?.giornoSettimana),
      mansione: item?.mansione || "",
      fascia: item?.fascia || "",
      minPersone: Number(item?.minPersone || 0),
      maxPersone: Number(item?.maxPersone || 0),
    }));

    downloadJsonFile(`staff-config-${sanitizeFileToken(selectedLocale.nome || selectedLocale.codice)}.json`, {
      schemaVersion: LOCALI_EXPORT_SCHEMA_VERSION,
      section: "staff-config",
      localeId: selectedLocaleId,
      localeCodice: selectedLocale.codice || "",
      exportedAt: new Date().toISOString(),
      staffConfigs: payload,
    });
    showNotification("Export configurazione staff completato", "success");
  };

  const importStaffJson = async (event) => {
    const selectedFile = event?.target?.files?.[0];
    if (!selectedFile) {
      return;
    }
    if (!selectedLocaleId) {
      showNotification("Seleziona un locale prima di importare la configurazione staff", "warning");
      return;
    }

    try {
      const parsed = await readJsonFromFile(selectedFile);
      const entries = Array.isArray(parsed)
        ? parsed
        : Array.isArray(parsed?.staffConfigs)
          ? parsed.staffConfigs
          : [];

      if (!entries.length) {
        throw new Error("Il file non contiene configurazioni staff da importare.");
      }

      const keyOf = (row) => `${normalizeDayOfWeek(row?.giornoSettimana)}|${row?.fascia}|${row?.mansione}`;
      const targetByKey = new Map((staffConfigs || []).map((row) => [keyOf(row), row]));
      let created = 0;
      let updated = 0;

      for (const rawItem of entries) {
        const giornoSettimana = normalizeDayOfWeek(rawItem?.giornoSettimana);
        const mansione = String(rawItem?.mansione || "").trim().toUpperCase();
        const fascia = String(rawItem?.fascia || "").trim().toUpperCase();

        if (!giornoSettimana || !mansione || !fascia) {
          throw new Error("Ogni configurazione staff deve contenere giornoSettimana, mansione e fascia.");
        }

        const body = {
          giornoSettimana,
          mansione,
          fascia,
          minPersone: Number(rawItem?.minPersone || 0),
          maxPersone: Number(rawItem?.maxPersone || 0),
        };

        const existing = targetByKey.get(keyOf(body));
        if (existing?.id) {
          await httpClient.put(`/locali/${selectedLocaleId}/staff-config/${existing.id}`, body);
          updated += 1;
        } else {
          await httpClient.post(`/locali/${selectedLocaleId}/staff-config`, body);
          created += 1;
        }
      }

      await loadStaffConfig(selectedLocaleId);
      showNotification(`Import configurazione staff completato (${created} creati, ${updated} aggiornati)`, "success");
    } catch (error) {
      showNotification(extractApiError(error), "error");
    }
  };

  const localeColumns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 80 },
      { field: "nome", headerName: "Nome", minWidth: 170, flex: 1 },
      { field: "codice", headerName: "Codice", minWidth: 120, flex: 0.8 },
      { field: "citta", headerName: "Citta", minWidth: 140, flex: 0.8 },
      {
        field: "attivo",
        headerName: "Attivo",
        minWidth: 100,
        valueGetter: (params) => (params.row.attivo ? "Si" : "No"),
      },
      {
        field: "actions",
        type: "actions",
        width: 110,
        getActions: (params) => [
          <GridActionsCellItem
            icon={<SettingsSuggestIcon />}
            label="Orari"
            onClick={() => setSelectedLocaleId(params.id)}
          />,
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Modifica"
            onClick={() => setLocaleModal({ open: true, value: params.row })}
          />,
          <GridActionsCellItem
            icon={<ContentCopyIcon />}
            label="Duplica"
            onClick={() => duplicateLocaleFromRow(params.row)}
          />,
          <GridActionsCellItem icon={<DeleteIcon />} label="Elimina" onClick={() => deleteLocale(params.id)} />,
        ],
      },
    ],
    []
  );

  const orariColumns = useMemo(
    () => [
      { field: "dayOfWeek", headerName: "Giorno", minWidth: 140, flex: 1 },
      { field: "oraApertura", headerName: "Apertura", minWidth: 120, flex: 0.7 },
      { field: "oraChiusura", headerName: "Chiusura", minWidth: 120, flex: 0.7 },
      {
        field: "aperto",
        headerName: "Aperto",
        minWidth: 100,
        valueGetter: (params) => (params.row.aperto ? "Si" : "No"),
      },
      {
        field: "actions",
        type: "actions",
        width: 80,
        getActions: (params) => [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Modifica"
            onClick={() => setOrarioModal({ open: true, value: params.row })}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Elimina"
            onClick={() => setDeleteHourModal({ open: true, value: params.row.dayOfWeek })}
          />,
        ],
      },
    ],
    [selectedLocaleId]
  );

  const staffConfigColumns = useMemo(
    () => [
      {
        field: "giornoSettimana",
        headerName: "Giorno",
        minWidth: 130,
        flex: 0.8,
        valueGetter: (params) =>
          dayOptions.find((d) => d.value === params.row.giornoSettimana)?.label || params.row.giornoSettimana,
      },
      { field: "mansione", headerName: "Mansione", minWidth: 120, flex: 0.8 },
      { field: "fascia", headerName: "Fascia", minWidth: 100, flex: 0.6 },
      { field: "minPersone", headerName: "Min", minWidth: 80, flex: 0.4 },
      { field: "maxPersone", headerName: "Max", minWidth: 80, flex: 0.4 },
      {
        field: "actions",
        type: "actions",
        width: 100,
        getActions: (params) => [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Modifica"
            onClick={() => setStaffConfigModal({ open: true, value: params.row })}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Elimina"
            onClick={() => deleteStaffConfig(params.id)}
          />,
        ],
      },
    ],
    [deleteStaffConfig]
  );

  return (
    <PageShell>
      <PageHeader
        title="Locali e orari"
        subtitle="Gestione filiali, configurazione orari e selezione operativa nel medesimo stile dei turni."
        actions={
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={() => duplicateLocaleFromRow(locali.find((l) => Number(l.id) === Number(selectedLocaleId)))}
              disabled={!selectedLocaleId}
            >
              Duplica locale selezionato
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setLocaleModal({ open: true, value: defaultLocale })}
            >
              Nuovo locale
            </Button>
          </Stack>
        }
      />

      <input
        ref={localiImportInputRef}
        type="file"
        accept="application/json,.json"
        onChange={importLocaliJson}
        style={{ display: "none" }}
      />
      <input
        ref={orariImportInputRef}
        type="file"
        accept="application/json,.json"
        onChange={importOrariJson}
        style={{ display: "none" }}
      />
      <input
        ref={staffImportInputRef}
        type="file"
        accept="application/json,.json"
        onChange={importStaffJson}
        style={{ display: "none" }}
      />

      <PagePanel tone="cool" sx={{ p: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography sx={pageTypography.panelTitle}>Elenco locali</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => openImportDialog(localiImportInputRef)}
            >
              Importa JSON
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportLocaliJson}
              disabled={!locali.length}
            >
              Esporta JSON
            </Button>
          </Stack>
        </Stack>
        {loading ? (
          <Skeleton variant="rounded" height={420} />
        ) : (
          <DataGrid
            rows={locali}
            columns={localeColumns}
            autoHeight
            disableRowSelectionOnClick
            onRowClick={(params) => setSelectedLocaleId(params.id)}
            pageSizeOptions={[10, 25, 50]}
            sx={dataGridSx}
          />
        )}
      </PagePanel>

      <PagePanel tone="warm" sx={{ p: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography sx={pageTypography.panelTitle}>Orari locale selezionato</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => openImportDialog(orariImportInputRef)}
              disabled={!selectedLocaleId}
            >
              Importa JSON
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportOrariJson}
              disabled={!selectedLocaleId || !orari.length}
            >
              Esporta JSON
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={() => setOrariDuplicaOpen(true)}
              disabled={!selectedLocaleId || locali.length < 2}
            >
              Duplica da locale
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setOrarioModal({ open: true, value: defaultOrario })}
              disabled={!selectedLocaleId}
            >
              Nuovo orario
            </Button>
          </Stack>
        </Stack>
        <DataGrid rows={orari} columns={orariColumns} autoHeight disableRowSelectionOnClick pageSizeOptions={[7, 14]} sx={dataGridSx} />
      </PagePanel>

      <LocaleModal
        open={localeModal.open}
        value={localeModal.value}
        onClose={() => setLocaleModal({ open: false, value: defaultLocale })}
        onSubmit={saveLocale}
      />

      <OrarioModal
        open={orarioModal.open}
        value={orarioModal.value}
        onClose={() => setOrarioModal({ open: false, value: defaultOrario })}
        onSubmit={saveOrario}
      />

      <Dialog
        open={deleteHourModal.open}
        onClose={() => setDeleteHourModal({ open: false, value: null })}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Elimina orario</DialogTitle>
        <DialogContent>
          <Typography>Confermi l'eliminazione dell'orario selezionato?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteHourModal({ open: false, value: null })}>Annulla</Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              const dayOfWeek = deleteHourModal.value;
              setDeleteHourModal({ open: false, value: null });
              await deleteOrario(dayOfWeek);
            }}
          >
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      <PagePanel tone="cool" sx={{ p: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography sx={pageTypography.panelTitle}>Configurazione staff (min/max per mansione)</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => openImportDialog(staffImportInputRef)}
              disabled={!selectedLocaleId}
            >
              Importa JSON
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportStaffJson}
              disabled={!selectedLocaleId || !staffConfigs.length}
            >
              Esporta JSON
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={() => setStaffDuplicaOpen(true)}
              disabled={!selectedLocaleId || locali.length < 2}
            >
              Duplica da locale
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setStaffConfigModal({ open: true, value: defaultStaffConfig })}
              disabled={!selectedLocaleId}
            >
              Nuova configurazione
            </Button>
          </Stack>
        </Stack>
        <DataGrid
          rows={staffConfigs}
          columns={staffConfigColumns}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          sx={dataGridSx}
        />
      </PagePanel>

      <StaffConfigModal
        open={staffConfigModal.open}
        value={staffConfigModal.value}
        onClose={() => setStaffConfigModal({ open: false, value: defaultStaffConfig })}
        onSubmit={saveStaffConfig}
      />

      <Dialog open={orariDuplicaOpen} onClose={() => setOrariDuplicaOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Duplica orari da locale</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Locale sorgente"
            value={orariDuplicaSourceLocaleId}
            onChange={(event) => setOrariDuplicaSourceLocaleId(event.target.value)}
            sx={{ mt: 1 }}
          >
            {locali
              .filter((locale) => Number(locale.id) !== Number(selectedLocaleId))
              .map((locale) => (
                <MenuItem key={locale.id} value={String(locale.id)}>
                  {locale.nome} ({locale.codice || "N/A"})
                </MenuItem>
              ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrariDuplicaOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={duplicateOrariFromLocale} disabled={!orariDuplicaSourceLocaleId}>
            Duplica
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={staffDuplicaOpen} onClose={() => setStaffDuplicaOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Duplica configurazione staff da locale</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Locale sorgente"
            value={staffDuplicaSourceLocaleId}
            onChange={(event) => setStaffDuplicaSourceLocaleId(event.target.value)}
            sx={{ mt: 1 }}
          >
            {locali
              .filter((locale) => Number(locale.id) !== Number(selectedLocaleId))
              .map((locale) => (
                <MenuItem key={locale.id} value={String(locale.id)}>
                  {locale.nome} ({locale.codice || "N/A"})
                </MenuItem>
              ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStaffDuplicaOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={duplicateStaffConfigFromLocale} disabled={!staffDuplicaSourceLocaleId}>
            Duplica
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
